import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { btnGhost, btnPrimary, inputCls } from "@/components/TopBar";

const publicUrl = (path) => `${window.location.origin}${path}`;

export const FleetShareDialog = ({ vehicles, onClose }) => {
  const [selected, setSelected] = useState(vehicles.map((v) => v.id));
  const [links, setLinks] = useState([]);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const allSelected = selected.length === vehicles.length && vehicles.length > 0;

  const load = useCallback(async () => {
    const { data } = await api.get("/shares", { params: { scope: "fleet" } });
    setLinks(data);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load fleet links"));
  }, [load]);

  const copy = async (path) => {
    const url = publicUrl(path);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.message(url);
    }
  };

  const create = async () => {
    if (selected.length === 0) return toast.error("Select at least one truck");
    setBusy(true);
    try {
      const { data } = await api.post("/shares/fleet", {
        vehicle_ids: selected,
        label: label.trim() || null,
        expires_days: Number(days) || 7,
      });
      toast.success(`Fleet link created for ${selected.length} truck(s)`);
      copy(data.path);
      setLabel("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/shares/${id}`);
      toast.success("Link revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[1700] flex items-start justify-center overflow-y-auto p-4 py-10" data-testid="fleet-share-dialog">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-lg border border-white/15 bg-[#0A0A0A] p-6 rise">
        <button
          onClick={onClose}
          data-testid="close-fleet-share-btn"
          className="absolute right-3 top-3 border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF]"
          aria-label="Close"
        >
          <X size={14} />
        </button>
        <p className="overline">client access</p>
        <h2 className="font-head text-2xl font-black tracking-tight">SHARE FLEET</h2>
        <p className="mt-2 text-[11px] text-[#A1A1AA]">
          One link, several trucks. The client sees every selected truck on one map with its status - no
          login, no driver contact details.
        </p>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <label className="overline">trucks · {selected.length} selected</label>
            <button
              data-testid="fleet-share-select-all-btn"
              onClick={() => setSelected(allSelected ? [] : vehicles.map((v) => v.id))}
              className={btnGhost}
            >
              {allSelected ? "CLEAR ALL" : "SELECT ALL"}
            </button>
          </div>
          <div className="doot-scroll mt-2 max-h-40 overflow-y-auto border border-white/15 bg-[#121212]" data-testid="fleet-share-vehicle-list">
            {vehicles.map((v) => (
              <label
                key={v.id}
                data-testid={`fleet-share-option-${v.plate_number}`}
                className="flex cursor-pointer items-center gap-2 border-b border-white/5 px-3 py-2 text-[11px] hover:bg-[#1A1A1A]"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(v.id)}
                  onChange={() =>
                    setSelected((prev) => (prev.includes(v.id) ? prev.filter((x) => x !== v.id) : [...prev, v.id]))
                  }
                  className="h-3 w-3 accent-[#00F0FF]"
                />
                <span className="font-head font-bold">{v.plate_number}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="overline mb-2 block">label (optional)</label>
            <input
              data-testid="fleet-share-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Acme Steel - all trucks"
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">expires in (days)</label>
            <input
              data-testid="fleet-share-days-input"
              type="number"
              min="1"
              max="90"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <button onClick={create} disabled={busy} data-testid="create-fleet-share-btn" className={`${btnPrimary} mt-5 w-full py-3`}>
          <Link2 size={12} /> {busy ? "creating" : "create fleet link"}
        </button>

        <p className="overline mt-6 mb-2">existing fleet links</p>
        <div className="doot-scroll max-h-48 overflow-y-auto border border-white/10" data-testid="fleet-share-list">
          {links.length === 0 && (
            <p className="px-3 py-5 text-xs text-[#52525B]" data-testid="fleet-share-empty">
              NO FLEET LINKS YET
            </p>
          )}
          {links.map((l) => (
            <div key={l.id} data-testid={`fleet-share-row-${l.id}`} className="flex items-center gap-2 border-b border-white/5 px-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px]" style={{ color: l.revoked ? "#52525B" : "#00F0FF" }}>
                  {publicUrl(l.path)}
                </p>
                <p className="overline mt-1">
                  {l.revoked ? "revoked" : `${l.views} views`} · {l.vehicle_ids.length} trucks ·{" "}
                  {l.label || "no label"}
                </p>
              </div>
              {!l.revoked && (
                <>
                  <button onClick={() => copy(l.path)} data-testid={`copy-fleet-share-btn-${l.id}`} className={btnGhost} aria-label="Copy link">
                    <Copy size={11} />
                  </button>
                  <button
                    onClick={() => revoke(l.id)}
                    data-testid={`revoke-fleet-share-btn-${l.id}`}
                    className="border border-white/10 p-1.5 text-[#A1A1AA] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                    aria-label="Revoke link"
                  >
                    <Trash2 size={11} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
